import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';

/**
 * AvailabilityEditor — manages a staff member's weekly availability.
 *
 * Props:
 *  - staffId: optional — used by admins to edit another staff member's
 *    availability. Staff users omit this and the backend defaults to
 *    their own profile.
 *  - canEdit: bool — when false, renders a read-only summary (e.g. for
 *    admins viewing a staff member outside the edit modal).
 *  - title: optional override.
 *
 * Visual layout:
 *  Top — list of existing availability rows (day, time range, notes,
 *        edit/delete actions).
 *  Bottom — inline "Add availability" form.
 */
const DAYS = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
];

const empty = () => ({
  day_of_week: 0,
  start_time: '09:00',
  end_time: '17:00',
  notes: '',
});

export function AvailabilityEditor({ staffId, canEdit = true, title }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getAvailability(staffId);
      setRows(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load availability');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [staffId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.start_time >= form.end_time) {
      setError('Start time must be before end time');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editingId) {
        await api.updateAvailability(editingId, {
          day_of_week: parseInt(form.day_of_week, 10),
          start_time: form.start_time,
          end_time: form.end_time,
          notes: form.notes || null,
        });
      } else {
        await api.createAvailability({
          staff_id: staffId || undefined,
          day_of_week: parseInt(form.day_of_week, 10),
          start_time: form.start_time,
          end_time: form.end_time,
          notes: form.notes || null,
        });
      }
      setForm(empty());
      setEditingId(null);
      await load();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (r) => {
    setEditingId(r._id);
    setForm({
      day_of_week: r.day_of_week,
      start_time: r.start_time,
      end_time: r.end_time,
      notes: r.notes || '',
    });
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Remove ${DAYS[r.day_of_week]} ${r.start_time}–${r.end_time}?`)) return;
    try {
      await api.deleteAvailability(r._id);
      await load();
    } catch (err) {
      alert(err.message || 'Failed to delete');
    }
  };

  return (
    <div data-testid={`availability-editor${staffId ? `-${staffId}` : ''}`}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>{title || '📅 Weekly availability'}</h3>
        {!canEdit && rows.length === 0 && !loading && (
          <span style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
            None declared
          </span>
        )}
      </div>
      <p style={{ marginTop: '0.25rem', fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
        Declare which days &amp; times you can work. Admins use this when scheduling shifts.
      </p>

      {error && (
        <div className="error-message" data-testid="availability-error" style={{ margin: '0.5rem 0' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div data-testid="availability-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="empty-state" style={{ padding: '0.75rem', marginBottom: '0.75rem' }}>
          No availability declared yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1rem' }}>
          {rows.map((r) => (
            <div
              key={r._id}
              data-testid={`availability-row-${r._id}`}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: '0.5rem', flexWrap: 'wrap',
                padding: '0.6rem 0.85rem',
                background: 'hsl(var(--muted))',
                border: '1px solid hsl(var(--border))',
                borderRadius: 'var(--radius)',
              }}
            >
              <div>
                <strong>{DAYS[r.day_of_week]}</strong>{' '}
                <span style={{ color: 'hsl(var(--muted-foreground))' }}>
                  {r.start_time} – {r.end_time}
                </span>
                {r.notes && (
                  <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))' }}>
                    {r.notes}
                  </div>
                )}
              </div>
              {canEdit && (
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => handleEdit(r)}
                    data-testid={`availability-edit-${r._id}`}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => handleDelete(r)}
                    style={{ color: 'hsl(0 70% 45%)' }}
                    data-testid={`availability-delete-${r._id}`}
                  >
                    🗑️
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {canEdit && (
        <form onSubmit={handleSubmit} style={{ background: 'hsl(var(--card))', border: '1px dashed hsl(var(--border))', borderRadius: 'var(--radius)', padding: '0.85rem' }} data-testid="availability-form">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Day</label>
              <select
                value={form.day_of_week}
                onChange={(e) => setForm({ ...form, day_of_week: e.target.value })}
                data-testid="availability-day"
              >
                {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>Start</label>
              <input
                type="time"
                value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                required
                data-testid="availability-start"
              />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>End</label>
              <input
                type="time"
                value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                required
                data-testid="availability-end"
              />
            </div>
            <div className="form-group" style={{ margin: 0, gridColumn: '1 / -1' }}>
              <label>Notes (optional)</label>
              <input
                type="text"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="e.g. Mornings only, can cover lunch"
                data-testid="availability-notes"
              />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.4rem', marginTop: '0.75rem' }}>
            {editingId && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => { setEditingId(null); setForm(empty()); setError(null); }}
                data-testid="availability-cancel"
              >
                Cancel
              </button>
            )}
            <button type="submit" className="btn btn-primary" disabled={saving} data-testid="availability-save">
              {saving ? 'Saving…' : editingId ? 'Update' : '+ Add availability'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default AvailabilityEditor;
